/**
 * SyncStatusIndicator — Ícone inline que mostra o status de sincronização
 * de um item específico salvo offline.
 *
 * Usage:
 *   <SyncStatusIndicator actionId={savedActionId} />
 *   // ou com status direto:
 *   <SyncStatusIndicator status="pending" />
 */
import { useState, useEffect } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertTriangle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { OfflineAction } from '@/lib/offlineDb';

type SyncStatus = OfflineAction['status'] | 'synced';

interface SyncStatusIndicatorProps {
  /** ID da ação na fila offline — monitora status automaticamente */
  actionId?: string;
  /** Status direto (alternativa a actionId) */
  status?: SyncStatus;
  /** Tamanho do ícone em px (padrão: 16) */
  size?: number;
  /** Mostrar label de texto ao lado do ícone */
  showLabel?: boolean;
}

const statusConfig: Record<SyncStatus, {
  icon: typeof Cloud;
  colorClass: string;
  labelKey: string;
  fallbackLabel: string;
  animate?: boolean;
}> = {
  pending: {
    icon: CloudOff,
    colorClass: 'text-amber-500',
    labelKey: 'sync.pending',
    fallbackLabel: 'Pendente — será sincronizado',
  },
  syncing: {
    icon: RefreshCw,
    colorClass: 'text-primary',
    labelKey: 'sync.syncing',
    fallbackLabel: 'Sincronizando...',
    animate: true,
  },
  failed: {
    icon: AlertTriangle,
    colorClass: 'text-destructive',
    labelKey: 'sync.failed',
    fallbackLabel: 'Falha ao sincronizar',
  },
  done: {
    icon: Check,
    colorClass: 'text-green-600 dark:text-green-400',
    labelKey: 'sync.done',
    fallbackLabel: 'Sincronizado',
  },
  synced: {
    icon: Check,
    colorClass: 'text-green-600 dark:text-green-400',
    labelKey: 'sync.done',
    fallbackLabel: 'Sincronizado',
  },
};

export function SyncStatusIndicator({
  actionId,
  status: directStatus,
  size = 16,
  showLabel = false,
}: SyncStatusIndicatorProps) {
  const { t } = useTranslation();
  const [trackedStatus, setTrackedStatus] = useState<SyncStatus>(
    directStatus ?? 'pending'
  );

  // Track status changes via actionId
  useEffect(() => {
    if (!actionId) return;

    let mounted = true;

    const checkStatus = async () => {
      try {
        const { getAllQueuedActions } = await import('@/lib/offlineDb');
        const actions = await getAllQueuedActions();
        const action = actions.find((a) => a.id === actionId);
        if (!mounted) return;

        if (!action) {
          // Action removed from queue = successfully synced
          setTrackedStatus('synced');
        } else {
          setTrackedStatus(action.status);
        }
      } catch {
        // IndexedDB unavailable
      }
    };

    checkStatus();

    const handleChange = () => checkStatus();
    window.addEventListener('offline-queue-changed', handleChange);

    // Poll as fallback
    const interval = setInterval(checkStatus, 5000);

    return () => {
      mounted = false;
      window.removeEventListener('offline-queue-changed', handleChange);
      clearInterval(interval);
    };
  }, [actionId]);

  // Use direct status prop when provided
  useEffect(() => {
    if (directStatus !== undefined) {
      setTrackedStatus(directStatus);
    }
  }, [directStatus]);

  const config = statusConfig[trackedStatus];
  const Icon = config.icon;
  const label = t(config.labelKey, config.fallbackLabel);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1.5 ${config.colorClass}`}
            role="status"
            aria-label={label}
          >
            <Icon
              style={{ width: size, height: size }}
              className={config.animate ? 'animate-spin' : ''}
              aria-hidden="true"
            />
            {showLabel && (
              <span className="text-xs font-medium">{label}</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
